import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import QuickMenu from '../../components/QuickMenu/QuickMenu';
import { fallbackMahalleler } from './Mahalleler';
import { getNeighborhoodDevelopments } from '../../api/neighborhoodService';
import './MahalleDetail.scss';
import 'swiper/css';
import 'swiper/css/navigation';

const getImageUrl = (url) => {
  if (!url) return '/assets/images/default-image.jpg';
  if (url.startsWith('http')) return url;

  if (url.startsWith('/assets/')) return `https://iskenderun.bel.tr${url}`;

  if (url.startsWith('/uploads/')) {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://api.iskenderun.bel.tr/api';
    const apiDomain = apiUrl.endsWith('/api') ? apiUrl.slice(0, -4) : apiUrl;
    return `${apiDomain}${url}`;
  }

  return `https://iskenderun.bel.tr${url.startsWith('/') ? url : `/${url}`}`;
};

const buildCategorySections = (item, urlTag) => {
  if (Array.isArray(item?.categories) && item.categories.length > 0) {
    const categorySections = item.categories
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      .map((category, categoryIndex) => ({
        key: category.guid || `category-${categoryIndex}`,
        name: category.name || 'Genel',
        description: category.description || '',
        coverImage: category.images?.[0]?.imageUrl ? getImageUrl(category.images[0].imageUrl) : null,
        images: (category.images || []).map((image, imageIndex) => ({
          src: getImageUrl(image.imageUrl),
          alt: image.caption || `${item.name} - ${category.name || 'Genel'} - Fotoğraf ${imageIndex + 1}`
        }))
      }));

    if (categorySections.length > 0) {
      return categorySections;
    }
  }

  if (Array.isArray(item?.images) && item.images.length > 0) {
    const groupedImages = item.images.reduce((acc, image) => {
      const key = image.categoryGuid || image.categoryName || 'genel';
      const existing = acc[key] || {
        key,
        name: image.categoryName || 'Genel',
        description: '',
        coverImage: null,
        images: []
      };

      const normalizedImage = {
        src: getImageUrl(image.imageUrl),
        alt: image.caption || `${item.name} - ${image.categoryName || 'Genel'}`
      };

      existing.images.push(normalizedImage);
      if (!existing.coverImage) {
        existing.coverImage = normalizedImage.src;
      }

      acc[key] = existing;
      return acc;
    }, {});

    const derivedCategories = Object.values(groupedImages);
    if (derivedCategories.length > 1 || (derivedCategories.length === 1 && derivedCategories[0].name !== 'Genel')) {
      return derivedCategories;
    }

    return [
      {
        key: 'genel',
        name: 'Genel',
        description: '',
        coverImage: getImageUrl(item.images[0].imageUrl),
        images: item.images.map((image, imageIndex) => ({
          src: getImageUrl(image.imageUrl),
          alt: image.caption || `${item.name} - Fotoğraf ${imageIndex + 1}`
        }))
      }
    ];
  }

  const total = item?.totalImages || 0;
  const images = Array.from({ length: total }, (_, index) => ({
    src: `https://iskenderun.bel.tr/assets/images/mahalleler/${urlTag}/${index + 1}.jpg`,
    alt: `${item?.name || 'Mahalle'} - Fotoğraf ${index + 1}`
  }));

  return [
    {
      key: 'genel',
      name: 'Genel',
      description: '',
      coverImage: images[0]?.src || null,
      images
    }
  ];
};

const MahalleDetail = () => {
  const { urlTag } = useParams();
  const [mahalleData, setMahalleData] = useState(null);
  const [categorySections, setCategorySections] = useState([]);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [mainSwiper, setMainSwiper] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let items = [];
        try {
          const response = await getNeighborhoodDevelopments();
          if (response?.isSuccess && Array.isArray(response.data)) {
            items = response.data;
          }
        } catch (error) {
          console.error('API error', error);
        }

        if (items.length === 0) {
          items = fallbackMahalleler;
        }

        const found = items.find((item) => item.urlTag === urlTag);
        if (!found) {
          setMahalleData({ name: 'Mahalle bulunamadı', title: '', description: '' });
          setCategorySections([]);
          setSelectedCategoryIndex(0);
          return;
        }

        const categories = buildCategorySections(found, urlTag);
        setMahalleData(found);
        setCategorySections(categories);
        setSelectedCategoryIndex(0);
        setSelectedImageIndex(0);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    fetchData();
  }, [urlTag]);

  const selectedCategory = useMemo(
    () => categorySections[selectedCategoryIndex] || categorySections[0] || null,
    [categorySections, selectedCategoryIndex]
  );

  const flatSlides = useMemo(() => {
    return categorySections.flatMap((category, categoryIndex) =>
      (category.images || []).map((image, imageIndex) => ({
        key: `${category.key}-${imageIndex}`,
        categoryIndex,
        imageIndex,
        categoryKey: category.key,
        categoryName: category.name,
        src: image.src,
        alt: image.alt
      }))
    );
  }, [categorySections]);

  const selectedFlatSlideIndex = useMemo(() => {
    if (!categorySections[selectedCategoryIndex]) return 0;

    const firstMatch = flatSlides.findIndex(
      (slide) => slide.categoryIndex === selectedCategoryIndex && slide.imageIndex === selectedImageIndex
    );

    if (firstMatch >= 0) {
      return firstMatch;
    }

    const firstCategorySlide = flatSlides.findIndex((slide) => slide.categoryIndex === selectedCategoryIndex);
    return firstCategorySlide >= 0 ? firstCategorySlide : 0;
  }, [categorySections, flatSlides, selectedCategoryIndex, selectedImageIndex]);

  const goToCategory = (categoryIndex) => {
    const firstSlideIndex = flatSlides.findIndex((slide) => slide.categoryIndex === categoryIndex);
    setSelectedCategoryIndex(categoryIndex);
    setSelectedImageIndex(0);
    if (mainSwiper && firstSlideIndex >= 0) {
      mainSwiper.slideTo(firstSlideIndex);
    }
  };

  return (
    <div className="mahalle-detail-page">
      <div className="container lg-py-5">
        <div className="row xs-mx-0 d-xs-block">
          <QuickMenu />

          <div className="col-md-10">
            {loading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
              </div>
            ) : (
              <>
                <section className="mahalle-detail-hero">
                  <div className="mahalle-detail-hero__label">Mahalle Detayı</div>
                  <h1 className="page-title mb-3">{mahalleData?.name}</h1>
                  {mahalleData?.title && <h3 className="mahalle-detail-hero__subtitle">{mahalleData.title}</h3>}
                  {mahalleData?.description && <p className="mahalle-detail-hero__description">{mahalleData.description}</p>}
                </section>

                <div className="mahalle-gallery mahalle-gallery--single">
                  {selectedCategory && (
                    <section className="mahalle-category-focus">
                      <div className="category-header">
                        <div>
                          <h3 className="category-title">{selectedCategory.name}</h3>
                          {selectedCategory.description && (
                            <p className="category-description">{selectedCategory.description}</p>
                          )}
                        </div>
                        <div className="category-count">{selectedCategory.images.length} fotoğraf</div>
                      </div>

                        <div className="gallery-slider">
                        {flatSlides.length > 0 ? (
                          <div className="main-slider mb-4">
                            <Swiper
                              modules={[Navigation, Autoplay]}
                              navigation
                              autoplay={{ delay: 3000, disableOnInteraction: false }}
                              className="main-swiper"
                              slidesPerView={1}
                              spaceBetween={0}
                              onSwiper={setMainSwiper}
                              onSlideChange={(swiper) => {
                                const activeSlide = flatSlides[swiper.activeIndex];
                                if (!activeSlide) return;
                                setSelectedCategoryIndex(activeSlide.categoryIndex);
                                setSelectedImageIndex(activeSlide.imageIndex);
                              }}
                              initialSlide={selectedFlatSlideIndex}
                            >
                              {flatSlides.map((slide) => (
                                <SwiperSlide key={slide.key} className="slide-item">
                                  <img src={slide.src} alt={slide.alt} className="rounded shadow-sm" />
                                </SwiperSlide>
                              ))}
                            </Swiper>
                          </div>
                        ) : (
                          <div className="category-empty-state mb-4">
                            Bu kategori için henüz görsel eklenmemiş.
                          </div>
                        )}

                        {selectedCategory?.images?.length > 1 && (
                          <div className="image-thumbnail-shell">
                            <Swiper
                              slidesPerView="auto"
                              spaceBetween={12}
                              className="image-thumbnail-swiper"
                            >
                              {selectedCategory.images.map((image, index) => (
                                <SwiperSlide key={`${selectedCategory.key}-thumb-${index}`}>
                                  <button
                                    type="button"
                                    className={`image-thumbnail-card ${index === selectedImageIndex ? 'is-active' : ''}`}
                                    onClick={() => {
                                      setSelectedImageIndex(index);
                                      if (mainSwiper) {
                                        const slideIndex = flatSlides.findIndex(
                                          (slide) => slide.categoryIndex === selectedCategoryIndex && slide.imageIndex === index
                                        );
                                        if (slideIndex >= 0) {
                                          mainSwiper.slideTo(slideIndex);
                                        }
                                      }
                                    }}
                                  >
                                    <img src={image.src} alt={image.alt} />
                                  </button>
                                </SwiperSlide>
                              ))}
                            </Swiper>
                          </div>
                        )}

                        <div className="category-slider-shell">
                          <Swiper
                            slidesPerView="auto"
                            spaceBetween={14}
                            className="category-swiper"
                          >
                            {categorySections.map((category, index) => (
                              <SwiperSlide key={category.key}>
                                <button
                                  type="button"
                                  className={`category-chip-card ${index === selectedCategoryIndex ? 'is-active' : ''}`}
                                  onClick={() => goToCategory(index)}
                                >
                                  <div className="category-chip-card__image">
                                    {category.coverImage ? (
                                      <img src={category.coverImage} alt={category.name} />
                                    ) : (
                                      <div className="category-chip-card__placeholder" />
                                    )}
                                  </div>
                                  <div className="category-chip-card__body">
                                    <strong>{category.name}</strong>
                                    <span>{category.images.length} fotoğraf</span>
                                  </div>
                                </button>
                              </SwiperSlide>
                            ))}
                          </Swiper>
                        </div>
                      </div>
                    </section>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MahalleDetail;
